import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { ImageQuestion } from "../../../../shared/components/dynamic-form/question-image";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { yesNoOptions } from "../../../../shared/constants/components";
import { pattern } from "../../../../shared/constants/patterns";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { PartnerService } from "../../partners/partner.service";
import { Partner } from "../../partners/partner.type";
import { Company } from "../company.type";

@Injectable()
export class CreateCompanyService {
  constructor(private partnerService: PartnerService) {}

  getPartner(partnerId: string): Observable<DropdownOption[]> {
    return this.partnerService.getPartner(partnerId).pipe(map((r: Partner) => [{ value: r.id, label: r.name }]));
  }

  getPartners(query?: { name: string }): Observable<DropdownOption[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(map((r: PaginatedResponse<Partner>) => r.items.map((c: Partner) => ({ value: c.id, label: c.name }))));
  }

  generateQuestions(options: {
    company?: Company;
    partnerValue?: string;
    partnerEdit?: boolean;
    partnerSkip?: boolean;
    canManageClientPriceEdit?: boolean;
    skipManageClientPrice?: boolean;
    showFairMarketValueAndBookValue?: boolean;
    operation?: Operation;
    deleteCompanyLogoModal?: () => void;
  }): Observable<QuestionBase<string | boolean>[]> {
    const isUpdating = options.operation === Operation.update;

    const questions: QuestionBase<string | boolean>[] = [
      !options.partnerSkip &&
        new DropdownQuestion({
          value: options?.partnerValue || options?.company?.partnerId,
          key: "partnerId",
          label: "Partner",
          disabled: isUpdating || !options.partnerEdit,
          required: true,
          currentOption: {
            label: options.company?.partner?.name,
            value: options.company?.partner?.id,
          },
          asyncOptions:
            options?.partnerValue || options?.company?.partnerId
              ? this.getPartner(options?.partnerValue || options?.company?.partnerId)
              : this.getPartners(),
          search: {
            filter: {
              callback: !options?.partnerEdit ? undefined : ({ value: name }) => this.getPartners({ name }),
            },
          },
          order: 1,
          copyToClipboard: isUpdating,
        }),

      options?.company &&
        new TextboxQuestion({
          value: options.company?.companyIdentifier,
          key: "companyIdentifier",
          label: "Company ID",
          required: true,
          disabled: true,
          order: 2,
          copyToClipboard: isUpdating,
        }),

      new TextboxQuestion({
        value: options?.company && options.company?.name,
        key: "name",
        label: "Name",
        required: true,
        order: 3,
        copyToClipboard: isUpdating,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(130),
            message: "Name is <strong>too long</strong>",
          },
        ],
      }),

      new TextboxQuestion({
        value: options.company?.type,
        key: "type",
        label: "Type",
        required: true,
        order: 4,
        copyToClipboard: isUpdating,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Type is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Type is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Type is <strong>too long</strong>",
          },
        ],
      }),

      new DropdownQuestion({
        value: options.company?.status,
        key: "status",
        label: "Status",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
        required: true,
        order: 5,
      }),

      !options.skipManageClientPrice &&
        new DropdownQuestion({
          value: options?.company?.canManageClientPrice ?? false,
          key: "canManageClientPrice",
          label: "Manage Internal Pricing",
          options: [...yesNoOptions],
          disabled: !options.canManageClientPriceEdit,
          required: false,
          order: 6,
        }),

      options.showFairMarketValueAndBookValue &&
        new DropdownQuestion({
          value: options?.company?.enableAssetCustomFields ?? false,
          key: "enableAssetCustomFields",
          label: "Fair Market Value and Book Value",
          options: [...yesNoOptions],
          required: false,
          order: 7,
        }),

      new ImageQuestion({
        value: options.company?.logo?.toString(),
        key: "logo",
        label: "Company Logo",
        required: false,
        ...(options.deleteCompanyLogoModal && { delete: { show: true, callback: options.deleteCompanyLogoModal } }),
        order: 8,
        imageReqs: {
          fileRestrictions: {
            maxSize: 500000, // 500 KB
            maxWidth: 1500,
            maxHeight: 300,
          },
          imageName: "",
          fileTooBig: false,
          invalidDimensions: false,
        },
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }
}
